// packages/content/src/look.ts (M0-T06) — character creator options, roof swatches and the friend colours
import type { FriendColor } from './schema/domain';
import type { FriendColorSpec, LookOptions } from './schema/look';

export const look: LookOptions = {
  skins: [
    { id: 'skin1', hex: '#FBE3CF', name: 'look.skin1' }, { id: 'skin2', hex: '#F0C5A0', name: 'look.skin2' },
    { id: 'skin3', hex: '#D9A27A', name: 'look.skin3' }, { id: 'skin4', hex: '#B97A55', name: 'look.skin4' },
    { id: 'skin5', hex: '#8A5A3C', name: 'look.skin5' }, { id: 'skin6', hex: '#5E3B28', name: 'look.skin6' },
  ],
  eyes: [
    { id: 'round', name: 'look.eyesRound' }, { id: 'happy', name: 'look.eyesHappy' },
    { id: 'sleepy', name: 'look.eyesSleepy' }, { id: 'sparkle', name: 'look.eyesSparkle' },
  ],
  hairStyles: [
    { id: 'short', name: 'look.hairShort' }, { id: 'bob', name: 'look.hairBob' }, { id: 'bun', name: 'look.hairBun' },
    { id: 'curly', name: 'look.hairCurly' }, { id: 'long', name: 'look.hairLong' }, { id: 'buzz', name: 'look.hairBuzz' },
  ],
  hairColors: [
    { id: 'black', hex: '#2B2118', name: 'look.hairBlack' }, { id: 'brown', hex: '#5A3A28', name: 'look.hairBrown' },
    { id: 'chestnut', hex: '#7A4A2C', name: 'look.hairChestnut' }, { id: 'auburn', hex: '#9A4A2E', name: 'look.hairAuburn' },
    { id: 'blond', hex: '#D9B26A', name: 'look.hairBlond' }, { id: 'ginger', hex: '#C9703A', name: 'look.hairGinger' },
    { id: 'grey', hex: '#A8A39A', name: 'look.hairGrey' }, { id: 'lilac', hex: '#B9A3D9', name: 'look.hairLilac' },
  ],
  dyes: [
    { id: 'coral', hex: '#EE7A5B', name: 'look.dyeCoral' }, { id: 'sky', hex: '#5AA9E6', name: 'look.dyeSky' },
    { id: 'lilac', hex: '#A88BD8', name: 'look.dyeLilac' }, { id: 'marigold', hex: '#F2B544', name: 'look.dyeMarigold' },
    { id: 'teal', hex: '#3FA79F', name: 'look.dyeTeal' }, { id: 'rose', hex: '#E07BAA', name: 'look.dyeRose' },
    { id: 'leaf', hex: '#6CBE6B', name: 'look.dyeLeaf' }, { id: 'clay', hex: '#C98B5E', name: 'look.dyeClay' },
    { id: 'moss', hex: '#9DBA5A', name: 'look.dyeMoss' }, { id: 'plum', hex: '#B276A6', name: 'look.dyePlum' },
    { id: 'cream', hex: '#FFF4E2', name: 'look.dyeCream' }, { id: 'denim', hex: '#4A5A78', name: 'look.dyeDenim' },
    { id: 'charcoal', hex: '#3E3B3A', name: 'look.dyeCharcoal' }, { id: 'brown', hex: '#5E4733', name: 'look.dyeBrown' },
    { id: 'white', hex: '#F7F4EC', name: 'look.dyeWhite' }, { id: 'navy', hex: '#2F3E66', name: 'look.dyeNavy' },
  ],
  // revision 10 (D-04): the KayKit Medieval Hexagon roof colours, in ROOF_COLORS order. The hex values are close to the kit's roof
  // tones; re-sample them from the atlas `hexagons_medieval.png` once `pnpm assets` has run (they only fill the roof picker's
  // swatches; the 3D roof is the kit model itself).
  roofs: [
    { id: 'red', hex: '#C4553F', name: 'look.roofRed' }, { id: 'blue', hex: '#4E7FB4', name: 'look.roofBlue' },
    { id: 'yellow', hex: '#E0AE43', name: 'look.roofYellow' }, { id: 'green', hex: '#6AA35A', name: 'look.roofGreen' },
  ],
};

/** DESIGN.md tags/inks; teal, rose, leaf and clay dyes and both moss and plum are proposals (D-43, owner question §13). */
export const friendColors: Readonly<Record<FriendColor, FriendColorSpec>> = {
  coral: { tag: '#C84B30', dye: '#EE7A5B', inkOnWhite: '#C84B30', textOnTag: '#FFFFFF' },
  sky: { tag: '#2A74B2', dye: '#5AA9E6', inkOnWhite: '#2A74B2', textOnTag: '#FFFFFF' },
  lilac: { tag: '#8564C2', dye: '#A88BD8', inkOnWhite: '#8564C2', textOnTag: '#FFFFFF' },
  marigold: { tag: '#F2B544', dye: '#F2B544', inkOnWhite: '#9A6A08', textOnTag: '#4A3A2C' },
  teal: { tag: '#177D77', dye: '#3FA79F', inkOnWhite: '#177D77', textOnTag: '#FFFFFF' },
  rose: { tag: '#BC4A80', dye: '#E07BAA', inkOnWhite: '#BC4A80', textOnTag: '#FFFFFF' },
  leaf: { tag: '#367F40', dye: '#6CBE6B', inkOnWhite: '#367F40', textOnTag: '#FFFFFF' },
  clay: { tag: '#985F38', dye: '#C98B5E', inkOnWhite: '#985F38', textOnTag: '#FFFFFF' },
  moss: { tag: '#5E7F2A', dye: '#9DBA5A', inkOnWhite: '#4F6B22', textOnTag: '#FFFFFF' },
  plum: { tag: '#7A3B6E', dye: '#B276A6', inkOnWhite: '#6E3463', textOnTag: '#FFFFFF' },
};
